import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, CalendarClock, HandCoins, Loader2, Sparkles, Undo2 } from "lucide-react";
import { API, api, apiError, inr, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListState, PageHeader } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const TONE = {
  "Owner Escalation": "destructive", "Promise Broken": "destructive",
  "High Priority": "default", "Follow-up Due": "secondary", "Low Priority": "outline",
};

function ScoreBar({ score }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${score}%` }} />
      </div>
      <span className="font-mono-num text-xs">{score}</span>
    </div>
  );
}

function PromiseDialog({ row, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: String(row.outstanding || ""),
    promise_date: "", remarks: "", next_follow_up: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.promise_date) return toast.error("Pick the promised date");
    setBusy(true);
    try {
      await api.post("/collections/promises", { invoice_id: row.invoice_id,
        amount: Number(form.amount), promise_date: form.promise_date,
        remarks: form.remarks, next_follow_up: form.next_follow_up });
      toast.success("Promise recorded");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="promise-dialog">
        <DialogHeader>
          <DialogTitle>Promise to pay — {row.customer_name}</DialogTitle>
          <DialogDescription>
            {row.invoice_number} • {inr(row.outstanding)} outstanding
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Promised amount">
            <Input data-testid="promise-amount" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </Field>
          <Field label="Promised date">
            <Input type="date" data-testid="promise-date" value={form.promise_date}
              onChange={(e) => setForm({ ...form, promise_date: e.target.value })} />
          </Field>
          <Field label="Next follow-up">
            <Input type="date" data-testid="promise-follow-up" value={form.next_follow_up}
              onChange={(e) => setForm({ ...form, next_follow_up: e.target.value })} />
          </Field>
          <Field label="Remarks">
            <Input data-testid="promise-remarks" value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button disabled={busy} onClick={save} data-testid="promise-save">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save promise
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DraftDialog({ row, onClose }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.post("/collections/draft-message", { invoice_id: row.invoice_id, tone: "polite" })
      .then(({ data }) => setText(data.draft || data.facts))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [row.invoice_id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="draft-dialog">
        <DialogHeader>
          <DialogTitle>Reminder draft — {row.customer_name}</DialogTitle>
          <DialogDescription>
            Written by AI from your real invoice figures. Nothing is sent automatically.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <p className="whitespace-pre-wrap rounded border border-border p-3 text-sm"
            data-testid="draft-text">{text}</p>
        )}
        <DialogFooter>
          <Button variant="outline" data-testid="draft-copy"
            onClick={() => { navigator.clipboard?.writeText(text); toast.success("Copied"); }}>
            Copy message
          </Button>
          <Button asChild data-testid="draft-open-reminders">
            <Link to="/reminders">Open reminders</Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClosureDialog({ row, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/collections/closure/${row.invoice_id}`)
      .then(({ data: d }) => setData(d)).catch((e) => toast.error(apiError(e)));
  }, [row.invoice_id]);
  useEffect(() => { load(); }, [load]);

  const issue = async () => {
    setBusy(true);
    try {
      const { data: rec } = await api.post(
        `/collections/closure/${row.invoice_id}/final-receipt`);
      toast.success(`Final receipt ${rec.number} issued — PAID IN FULL`);
      load();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const thankYou = async (channel) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(
        `/collections/final-receipt/${data.final_receipt.id}/thank-you`, { channel });
      if (channel === "whatsapp" && res.whatsapp_link) window.open(res.whatsapp_link, "_blank");
      else toast.success("Thank-you email sent");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="closure-dialog">
        <DialogHeader>
          <DialogTitle>Full &amp; final closure — {row.invoice_number}</DialogTitle>
          <DialogDescription>
            A final receipt can only be issued when the balance is exactly ₹0.
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <div className="grid gap-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Invoice total</span>
              <span className="font-mono-num">{inr(data.grand_total)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Received</span>
              <span className="font-mono-num">{inr(data.paid)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Outstanding</span>
              <span className="font-mono-num">{inr(data.outstanding)}</span></div>
            {data.payments?.length > 0 && (
              <div className="mt-2 rounded border border-border p-2 text-xs"
                data-testid="closure-payments">
                {data.payments.map((p) => (
                  <div key={p.id} className="flex justify-between">
                    <span>{p.number} • {shortDate(p.date)} • {p.method}</span>
                    <span className="font-mono-num">{inr(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            {data.final_receipt && (
              <Badge variant="default" className="w-fit" data-testid="final-receipt-badge">
                Final receipt {data.final_receipt.number} • {data.final_receipt.status}
              </Badge>
            )}
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          {data?.paid_in_full && !data?.final_receipt && (
            <Button disabled={busy} onClick={issue} data-testid="issue-final-receipt">
              <BadgeCheck className="mr-1 h-4 w-4" /> Issue final receipt
            </Button>
          )}
          {data?.final_receipt && (
            <>
              <Button variant="outline" data-testid="final-receipt-pdf"
                onClick={() => window.open(
                  `${API}/collections/final-receipt/${data.final_receipt.id}/pdf`, "_blank")}>
                Receipt PDF
              </Button>
              <Button variant="outline" disabled={busy} data-testid="thank-you-whatsapp"
                onClick={() => thankYou("whatsapp")}>
                Thank you on WhatsApp
              </Button>
              <Button variant="outline" disabled={busy} data-testid="thank-you-email"
                onClick={() => thankYou("email")}>
                Thank you by email
              </Button>
              <Button variant="ghost" disabled={busy} data-testid="retention-task"
                onClick={async () => {
                  try {
                    await api.post("/collections/retention-task",
                      { customer_id: row.customer_id, days: 30 });
                    toast.success("Repeat-business follow-up set for 30 days");
                  } catch (e) { toast.error(apiError(e)); }
                }}>
                Set retention follow-up
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CollectionsPro() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promise, setPromise] = useState(null);
  const [draft, setDraft] = useState(null);
  const [closure, setClosure] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        api.get("/collections/priority"),
        api.get("/collections/today"),
      ]);
      setData(p.data);
      setToday(t.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadBrief = async () => {
    setBrief({ loading: true });
    try {
      const { data: b } = await api.get("/collections/brief");
      setBrief(b);
    } catch (e) {
      setBrief(null);
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="collections-pro">
      <PageHeader
        testId="collections-pro-header"
        title="AI Collections"
        subtitle={data ? `${inr(data.total_outstanding)} across ${data.count} open invoices`
          : "Priority worklist, promises and closures"}
        actions={
          <Button variant="outline" className="gap-1.5" onClick={loadBrief}
            data-testid="daily-brief-button">
            <Sparkles className="h-4 w-4" /> Daily brief
          </Button>
        }
      />

      <div className="p-4 sm:p-6">
        {brief && (
          <div className="surface mb-4 p-4" data-testid="daily-brief">
            {brief.loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <>
                <div className="grid gap-2 sm:grid-cols-4">
                  {[["Outstanding", brief.total_outstanding], ["Overdue", brief.total_overdue],
                    ["Expected today", brief.expected_today],
                    ["Expected this week", brief.expected_this_week]].map(([k, v]) => (
                    <div key={k}>
                      <div className="overline">{k}</div>
                      <div className="font-mono-num text-sm">{inr(v)}</div>
                    </div>
                  ))}
                </div>
                {brief.ai_insight ? (
                  <p className="mt-3 whitespace-pre-wrap text-sm" data-testid="brief-insight">
                    {brief.ai_insight}
                  </p>
                ) : (
                  <p className="mt-3 text-xs text-muted-foreground">
                    AI note unavailable right now — the figures above are computed from your
                    invoices.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <Tabs defaultValue="priority">
          <TabsList data-testid="collections-tabs">
            <TabsTrigger value="priority" data-testid="tab-priority">Priority</TabsTrigger>
            <TabsTrigger value="today" data-testid="tab-today">Today</TabsTrigger>
          </TabsList>

          <TabsContent value="priority">
            <ListState loading={loading} isEmpty={!data || data.rows.length === 0}
              emptyTitle="Nothing outstanding" emptyHint="Every invoice is settled.">
              <>
                <div className="mb-3 flex flex-wrap gap-2">
                  {Object.entries(data?.categories || {}).map(([k, v]) => (
                    <Badge key={k} variant={TONE[k] || "outline"} className="text-[10px]"
                      data-testid={`category-count-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                      {k}: {v}
                    </Badge>
                  ))}
                </div>
                <div className="surface overflow-x-auto" data-testid="priority-table">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Score</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Invoice</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                        <TableHead className="text-right">Overdue</TableHead>
                        <TableHead>What to do</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data?.rows || []).map((r) => (
                        <TableRow key={r.invoice_id} data-testid={`priority-row-${r.invoice_id}`}>
                          <TableCell>
                            <ScoreBar score={r.score} />
                            <Badge variant={TONE[r.category] || "outline"}
                              className="mt-1 text-[9px]">{r.category}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            <Link className="underline" to={`/customers/${r.customer_id}`}>
                              {r.customer_name}
                            </Link>
                          </TableCell>
                          <TableCell className="font-mono-num text-xs">
                            {r.invoice_number}
                            <div className="text-[10px] text-muted-foreground">
                              due {shortDate(r.due_date)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono-num text-sm">
                            {inr(r.outstanding)}
                          </TableCell>
                          <TableCell className="text-right text-xs">
                            {r.days_overdue}d
                            {r.broken_promises > 0 && (
                              <div className="text-[10px] text-destructive">
                                {r.broken_promises} broken
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="max-w-56 text-xs text-muted-foreground">
                            {r.recommended_action}
                          </TableCell>
                          <TableCell className="space-x-1 text-right">
                            <Button size="sm" variant="outline"
                              data-testid={`promise-button-${r.invoice_id}`}
                              onClick={() => setPromise(r)}>
                              <HandCoins className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="outline"
                              data-testid={`draft-button-${r.invoice_id}`}
                              onClick={() => setDraft(r)}>
                              <Sparkles className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost"
                              data-testid={`closure-button-${r.invoice_id}`}
                              onClick={() => setClosure(r)}>
                              Closure
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            </ListState>
          </TabsContent>

          <TabsContent value="today">
            <div className="grid gap-3 sm:grid-cols-2">
              {[["Follow-ups due", today?.follow_ups_due, "invoice_number"],
                ["Promises due today", today?.promises_due_today, "invoice_number"],
                ["Broken promises", today?.broken_promises, "invoice_number"],
                ["High priority", today?.high_priority, "invoice_number"]].map(([label, rows]) => (
                <div key={label} className="surface p-3"
                  data-testid={`today-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="overline flex items-center gap-1">
                    <CalendarClock className="h-3.5 w-3.5" /> {label}
                  </div>
                  {(rows || []).length === 0 ? (
                    <p className="mt-1 text-xs text-muted-foreground">Nothing here.</p>
                  ) : (
                    <div className="mt-1.5 grid gap-1">
                      {(rows || []).slice(0, 8).map((r, i) => (
                        <div key={`${label}-${i}`} className="flex justify-between text-xs">
                          <span>{r.customer_name} • {r.invoice_number}</span>
                          <span className="font-mono-num">
                            {inr(r.outstanding ?? r.amount ?? 0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="surface p-3 sm:col-span-2" data-testid="today-recent-payments">
                <div className="overline">Recent payments</div>
                {(today?.recent_payments || []).map((p) => (
                  <div key={p.id} className="flex justify-between text-xs">
                    <span>
                      {p.number} • {p.customer_name} • {shortDate(p.date)}
                      {p.reversed && (
                        <Badge variant="destructive" className="ml-1 text-[9px]">
                          <Undo2 className="mr-0.5 h-2.5 w-2.5" /> reversed
                        </Badge>
                      )}
                    </span>
                    <span className="font-mono-num">{inr(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {promise && <PromiseDialog row={promise} onClose={() => setPromise(null)} onSaved={load} />}
      {draft && <DraftDialog row={draft} onClose={() => setDraft(null)} />}
      {closure && (
        <ClosureDialog row={closure} onClose={() => setClosure(null)} onDone={load} />
      )}
    </div>
  );
}
